/**
 * Branch Data Mappers
 * Transform branch API responses to component-friendly format
 */

import type {
  Branch,
  BranchesResponse,
  BranchManager,
} from '../api/branch.service';

/**
 * Mapped branch manager for component use
 */
export interface MappedBranchManager {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nmls: string;
  photo: string | null;
  location: string;
  fullAddress: string;
  websiteUrl: string | null;
}

/**
 * Mapped branch for component use
 */
export interface MappedBranch {
  branchId: string;
  name: string;
  address: string; // Full address string
  addressLine: string; // Street address line only
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  branchNmls: string;
  websiteUrl: string;
  logo: string;
  photo: string | null;
  branchManagers: MappedBranchManager[];
}

/**
 * Mapped branches response
 */
export interface MappedBranchesResponse {
  branches: MappedBranch[];
  total: number;
  pageSize: number;
}

/**
 * Map single branch manager to component format
 * @param manager - Raw branch manager from API
 * @returns Mapped branch manager for components
 */
export const mapBranchManagerToComponentFormat = (
  manager: BranchManager
): MappedBranchManager => {
  const fullName = `${manager.firstName} ${manager.lastName}`;
  const location = `${manager.address.city}, ${manager.address.state}`;
  const fullAddress = `${manager.address.line}, ${manager.address.city}, ${manager.address.state} ${manager.address.zipCode}`;

  return {
    id: manager.id,
    name: fullName,
    firstName: manager.firstName,
    lastName: manager.lastName,
    email: manager.email,
    phone: manager.phone,
    nmls: manager.nmlsId,
    photo: manager.photoUrl,
    location,
    fullAddress,
    websiteUrl: manager.websiteUrl,
  };
};

/**
 * Map single branch to component format
 * @param branch - Raw branch from API
 * @returns Mapped branch for components
 */
export const mapBranchToComponentFormat = (branch: Branch): MappedBranch => {
  const fullAddress = `${branch.address.line}, ${branch.address.city}, ${branch.address.state} ${branch.address.zipCode}`;

  // Get photo: use branch photoUrl, or first branch manager's photo, or logoUrl as fallback
  const branchPhoto =
    branch.photoUrl ||
    branch.branchManagers?.[0]?.photoUrl ||
    branch.logoUrl ||
    null;

  return {
    branchId: branch.branchId,
    name: branch.name,
    address: fullAddress,
    addressLine: branch.address.line, // Street address line only
    city: branch.address.city,
    state: branch.address.state,
    zipCode: branch.address.zipCode,
    phone: branch.phone,
    email: branch.email,
    branchNmls: `#${branch.branchNmlsId}`,
    websiteUrl: branch.websiteUrl,
    logo: branch.logoUrl,
    photo: branchPhoto,
    branchManagers: branch.branchManagers.map(
      mapBranchManagerToComponentFormat
    ),
  };
};

/**
 * Map branches response to component format
 * @param response - Raw branches response from API
 * @returns Mapped response for components
 */
export const mapBranchesToComponentFormat = (
  response: BranchesResponse
): MappedBranchesResponse => {
  return {
    branches: response.branches.map(mapBranchToComponentFormat),
    total: response.totalCount,
    pageSize: response.pageSize,
  };
};

export default {
  mapBranchManagerToComponentFormat,
  mapBranchToComponentFormat,
  mapBranchesToComponentFormat,
};
